import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SegmentedControlOptionType } from '@fullstory/components';
import { AppDispatch, UserOverlayRequestActions, userOverlaySelectors } from '@fullstory/redux';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { UserDetailsResponse } from '@fullstory/client-api';
import { useNotifications } from '@fullstory/react-hooks';
import { intl } from '@fullstory/intl';
import { SessionTypes } from '../types';

// NB(vasilios): Fixes "error TS2742: inferred type cannot be named without a reference"
// See https://github.com/aspect-build/rules_ts/issues/159 for more information.
import type {} from '@fullstory/redux-util/types';

const { getAllSessions, getMatchingSessionsOrClips } = userOverlaySelectors;

type UseUserSessionData = {
  sessionFilter: SegmentedControlOptionType | null;
  userId: string;
  segmentDefinition: SegmentDefinition;
  eventSearch?: EventSearch;
};

export const useUserSessionData = ({
  sessionFilter,
  userId,
  segmentDefinition,
  eventSearch,
}: UseUserSessionData) => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const allSessions = useSelector(getAllSessions);
  const matchingSessionsOrClips = useSelector(getMatchingSessionsOrClips);
  // but conditionally swap the data for the session playlist based on the selected toggle
  const sessionData = useMemo((): UserDetailsResponse | null => {
    if (sessionFilter) {
      if (sessionFilter.id === SessionTypes.All) return allSessions.data;
      return matchingSessionsOrClips.data;
    }
    return null;
  }, [sessionFilter, matchingSessionsOrClips.data, allSessions.data]);

  // REQUEST SETUP
  // only request each once, after that, it's stored in redux
  const [requestSessionsOrClips, setRequestSessionsOrClips] = useState(true);
  const [requestAllSessions, setRequestAllSessions] = useState(true);
  useEffect(() => {
    let loadingPromise: Promise<any> | null = null;
    switch (sessionFilter?.id) {
      case SessionTypes.All:
        setRequestAllSessions(false);
        if (requestAllSessions)
          loadingPromise = dispatch(
            UserOverlayRequestActions.allSessions(userId, segmentDefinition, true),
          );
        break;
      case SessionTypes.MatchingSessions:
      case SessionTypes.MatchingClips:
      default:
        if (requestSessionsOrClips)
          loadingPromise = dispatch(
            UserOverlayRequestActions.matchingSessionsOrClips(
              userId,
              segmentDefinition,
              eventSearch,
              true,
            ),
          );
        setRequestSessionsOrClips(false);
        break;
    }
    loadingPromise?.catch(() => {
      notifications?.error(
        intl.formatMessage({
          defaultMessage:
            'Failed to load user. If the problem persists, drop us a note using the Support button.',
          id: 'f5PWJe',
        }),
        'FETCH_USER_DETAILS',
      );
    });
  }, [
    dispatch,
    userId,
    segmentDefinition,
    eventSearch,
    sessionFilter?.id,
    notifications,
    requestAllSessions,
    requestSessionsOrClips,
  ]);

  return { sessionData, allSessions };
};
